import { Component, Inject, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule } from "@angular/common";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import Swal from "sweetalert2";

import { GloDatePickerComponent } from "../../../shared/components/glo-date-picker/glo-date-picker/glo-date-picker.component";
import { Service } from "../../../shared/models/service.model";
import { Salon } from "../../../shared/models/salon.model";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { promoVoucher } from "../../../shared/models/promo.model";
import { ApiService } from "../../../shared/services/api/api.service";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { environment } from "../../../../environments/environment";

type PromoTab = "overview" | "schedule";

@Component({
  selector: "business-discount-add",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatSlideToggleModule,
    GloDatePickerComponent,
    LoaderComponent,
  ],
  templateUrl: "./discount-add.component.html",
  styleUrl: "./discount-add.component.scss",
})
export class BusinessDiscountAddComponent implements OnInit {
  assetUrl = environment.assetBaseUrl;

  promotionForm: FormGroup;

  isLoading = false;
  isUpdate = false;
  promoId: string | null = null;

  salons: Salon[] = [];
  services: Service[] = [];

  discountType = "PERCENT";
  activeTab: PromoTab = "overview";

  tabs: { key: PromoTab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "schedule", label: "Schedule" },
  ];

  inputClasses =
    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black";

  constructor(
    private apiService: ApiService,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<BusinessDiscountAddComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.promotionForm = this.fb.group({
      name: ["", Validators.required],
      code: ["", Validators.required],
      discount_type: ["PERCENT", Validators.required],
      discount_value: ["", [Validators.required, Validators.min(0), Validators.max(100)]],
      usage_limit_type: ["", Validators.required],
      salon: ["", Validators.required],
      service: ["", Validators.required],
      start_date: [null, Validators.required],
      end_date: [null, Validators.required],
      is_active: [true],
    });

    this.promotionForm.get("discount_type")?.valueChanges.subscribe((type) => {
      this.discountType = type || "PERCENT";
      this.updateDiscountValidators();
    });

    if (this.data?.promoId) {
      this.promoId = this.data.promoId;
      this.isUpdate = true;
      this.populatePromoForm(this.data.promoData);
      this.loadServices(this.data.promoData.salon);
    }
  }

  ngOnInit(): void {
    this.loadSalons();
  }

  loadSalons(): void {
    this.isLoading = true;

    this.apiService
      .request(API_ENDPOINTS.salons.list, "GET", null, "")
      .subscribe({
        next: (response: any) => {
          this.salons = response.results || [];
          this.isLoading = false;
        },
        error: (error) => {
          console.error("Error loading salons:", error);
          this.isLoading = false;
        },
      });
  }

  loadServices(event: Event | string): void {
    let salonId = "";

    if (typeof event === "string") {
      salonId = event;
    } else {
      salonId = (event.target as HTMLSelectElement).value;
      this.promotionForm.get("service")?.setValue("");
    }

    if (!salonId) {
      this.services = [];
      return;
    }

    this.isLoading = true;

    this.apiService
      .request(API_ENDPOINTS.salons.services.list(salonId), "GET", null, "")
      .subscribe({
        next: (response: any) => {
          this.services = response.results || [];
          this.isLoading = false;
        },
        error: (error) => {
          console.error("Error loading services:", error);
          this.isLoading = false;
        },
      });
  }

  discountTypeChanged(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.discountType = value;
    this.updateDiscountValidators();
  }

  private updateDiscountValidators(): void {
    const discountControl = this.promotionForm.get("discount_value");

    if (this.discountType === "PERCENT") {
      discountControl?.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]);
    } else {
      discountControl?.setValidators([Validators.required, Validators.min(0)]);
    }

    discountControl?.updateValueAndValidity();
  }

  onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.toUpperCase();
    this.promotionForm.get("code")?.setValue(input.value, { emitEvent: false });
  }

  populatePromoForm(data: promoVoucher): void {
    this.discountType = data.discount_type || "PERCENT";

    this.promotionForm.patchValue({
      name: data.name,
      code: data.code,
      discount_type: data.discount_type || "PERCENT",
      discount_value: data.discount_value,
      usage_limit_type: data.usage_limit_type,
      salon: data.salon,
      service: data.service,
      start_date: data.start_date,
      end_date: data.end_date,
      is_active: data.is_active,
    });

    this.updateDiscountValidators();
  }

  checkValidField(fieldName: string): boolean {
    const formField = this.promotionForm.get(fieldName);

    if (!formField) return true;

    return !(formField.invalid && formField.touched);
  }

  fetchInputField(controlName: string): FormControl {
    return this.promotionForm.get(controlName) as FormControl;
  }

  handleSave(event: Event): void {
    event.preventDefault();

    if (this.promotionForm.invalid) {
      this.promotionForm.markAllAsTouched();
      this.activeTab = this.hasScheduleErrors() ? "schedule" : "overview";
      return;
    }

    if (this.isUpdate && this.promoId) {
      this.updatePromoCode(this.promoId);
    } else {
      this.createPromoCode();
    }
  }

  private hasScheduleErrors(): boolean {
    return (
      !!this.promotionForm.get("start_date")?.invalid ||
      !!this.promotionForm.get("end_date")?.invalid
    );
  }

  allowNumbersOnly(event: Event): void {
    const e = event as KeyboardEvent;
    const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab"];

    if (allowedKeys.includes(e.key)) return;

    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  }

  createPromoCode(): void {
    this.isLoading = true;

    this.apiService
      .request(API_ENDPOINTS.owner.promoCode.create, "POST", this.promotionForm.value)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.dialogRef.close(true);

          Swal.fire({
            title: "Promo Added Successfully!",
            icon: "success",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
        error: (error) => {
          this.isLoading = false;

          Swal.fire({
            title: "Failed to Save Promo",
            text:
              error?.error?.code?.[0] ||
              "Something went wrong, please try again.",
            icon: "error",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
      });
  }

  updatePromoCode(id: string): void {
    this.isLoading = true;

    this.apiService
      .request(API_ENDPOINTS.owner.promoCode.update(id), "PATCH", this.promotionForm.value)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.dialogRef.close(true);

          Swal.fire({
            title: "Promo Updated Successfully!",
            icon: "success",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
        error: () => {
          this.isLoading = false;

          Swal.fire({
            title: "Failed to Update Promo",
            text: "Something went wrong, please try again.",
            icon: "error",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });
        },
      });
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  get modalTitle(): string {
    return this.isUpdate ? "Edit Promotion" : "Add Promotion";
  }

  get readableDiscountType(): string {
    if (this.discountType === "PERCENT") return "Percentage";
    if (this.discountType === "FIXED") return "Fixed Amount";
    return "Discount";
  }

  get formattedDiscountValue(): string {
    const value = Number(this.promotionForm.get("discount_value")?.value);

    if (this.discountType === "PERCENT") {
      return `${Number.isFinite(value) ? value : 0}%`;
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number.isFinite(value) ? value : 0);
  }
}